import { useEffect, useState } from 'react'

import { UseCompaniesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { useIsBecome } from 'modules/core/lib/useIsBecome'

import {
  type CompanyData,
  getCompanies as getFactorialIdCompanies,
} from 'modules/factorial_id/lib/api_client'

export interface Company {
  id: string
  name: string
  logo?: string
  factorial_id_id?: string
  external_id?: string
  cell?: string
  fid_enabled?: boolean
}

interface UseCompaniesResult {
  companies: Company[]
  loading: boolean
}

export const useCompanies = (): UseCompaniesResult => {
  const { company: currentCompany } = useCurrentSession()
  const isBecome = useIsBecome()

  const { data: monolithData, loading: monolithLoading } = useQuery(UseCompaniesDocument, {
    variables: { companyId: currentCompany.id },
  })

  const useFactorialId = monolithData?.features.enabledFeatures.nodes.some(
    (flag) => flag.enabled && flag.name === 'dev_factorial_id'
  )

  const [factorialIdCompanies, setFactorialIdCompanies] = useState<CompanyData[]>([])
  const [factorialIdLoading, setFactorialIdLoading] = useState(false)
  const [factorialIdError, setFactorialIdError] = useState(false)
  const [factorialIdFetched, setFactorialIdFetched] = useState(false)

  useEffect(() => {
    if (!useFactorialId || monolithLoading) {
      setFactorialIdFetched(false)
      return
    }

    const fetchFactorialIdCompanies = async () => {
      try {
        setFactorialIdLoading(true)
        setFactorialIdError(false)
        const data = await getFactorialIdCompanies()
        setFactorialIdCompanies(data)
      } catch (error) {
        setFactorialIdError(true)
        setFactorialIdCompanies([])
      } finally {
        setFactorialIdLoading(false)
        setFactorialIdFetched(true)
      }
    }

    fetchFactorialIdCompanies()
  }, [useFactorialId, monolithLoading])

  // Returning an empty list of companies can be dangerous because users of this
  // hook always expect at least one company, so in case of any errors we'll
  // return the current one.
  const getCurrentCompanyFallback = (): Company[] => [
    {
      id: currentCompany.id.toString(),
      name: currentCompany.name,
      logo: buildUrl(currentCompany.workspaceLogo),
      fid_enabled: false,
    },
  ]

  if (isBecome) {
    return { companies: getCurrentCompanyFallback(), loading: false }
  }

  if (monolithLoading) {
    return { companies: [], loading: true }
  }

  if (useFactorialId) {
    if (factorialIdLoading || !factorialIdFetched) {
      return { companies: [], loading: true }
    }

    if (factorialIdError || !factorialIdCompanies.length) {
      return { companies: getCurrentCompanyFallback(), loading: false }
    }

    return {
      companies: factorialIdCompanies.map((fc) => ({
        id: fc.external_id,
        name: fc.name,
        logo: fc.full_logo_url,
        factorial_id_id: fc.id,
        external_id: fc.external_id,
        cell: fc.cell,
        fid_enabled: fc.fid_enabled,
      })),
      loading: false,
    }
  }

  const monolithCompanies =
    monolithData?.companies.companiesConnection.nodes.map((c) => ({
      id: c.id.toString(),
      name: c.name,
      logo: buildUrl(c.workspaceLogo),
      fid_enabled: false,
    })) ?? []

  if (!monolithCompanies.length) {
    return { companies: getCurrentCompanyFallback(), loading: false }
  }

  return {
    companies: monolithCompanies,
    loading: false,
  }
}
